import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackOpenChatbot } from './tracking';
import { getNormalizedPath } from './utils';
import { openChat } from '../../actions/chatWindow';
import { setChatbotIsOpenEventSent, setChatbotUnreadMessagesCount } from '../../actions/chatbot/actions';
import { setMobileActiveSide } from '../../actions/mobileSide';
import { ChatListChatbotItem } from '../../components/ChatListChatbotItem';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { chatbotSelector, chatWindowSelector, openedChatsSelector } from '../../selectors';
import { sourceSelector } from '../../selectors/sourceSelector';
import { fetchChatbotReadMessages } from '../../services/сhatbot/fetchChatbotReadMessages';
import { EMobileLayoutActiveSide } from '../../types/mobileSide';
import { useApplicationContext } from '../../utils/applicationContext';
import { findLastMessage } from '../../utils/findLastMessage';

export const ChatListChatbotItemContainer = () => {
  const context = useApplicationContext();
  const { currentOpenedChatId } = useSelector(chatWindowSelector);
  const { unreadMessagesCount, isOpenEventSent } = useSelector(chatbotSelector);
  const source = useSelector(sourceSelector);

  const openedChats = useSelector(openedChatsSelector);

  const dispatch = useDispatch();

  const chatbotChat = openedChats[CHATBOT_ID];

  const isChatbotHelperEnabled = Boolean(context.config.get<boolean>('frontend-chats.ChatbotHelper.Enabled'));

  const ignoredChatbotOpenTrackingSources = React.useMemo(
    () => context.config.get<string[]>('frontend-chats.ignoredOpenTrackingSources') || [],
    [context.config],
  );

  const onChatbotClick = React.useCallback(() => {
    trackOpenChatbot();
    dispatch(setMobileActiveSide(EMobileLayoutActiveSide.Center));

    // тут осознанно нет await, так как клиент выставляет цифру
    fetchChatbotReadMessages(context);
    dispatch(setChatbotUnreadMessagesCount(0));

    dispatch(openChat(CHATBOT_ID));
  }, [dispatch, context]);

  React.useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    if (currentOpenedChatId === CHATBOT_ID) {
      queryParams.set('chatbot', '1');
    } else {
      queryParams.delete('chatbot');
    }

    if (currentOpenedChatId === CHATBOT_ID && !isOpenEventSent && source) {
      /**
       * Запрещаем отправку события открытия чатбота для определенных источников
       * (поскольку в этих местах отправляет это событие мкс-родитель)
       */
      if (!ignoredChatbotOpenTrackingSources.includes(source)) {
        trackOpenChatbot(source);
      }

      dispatch(setChatbotIsOpenEventSent(true));
    }

    const formattedPath = getNormalizedPath();

    window.history.replaceState(null, '', `${formattedPath}?${queryParams.toString()}`);
  }, [currentOpenedChatId, source, isOpenEventSent, dispatch, ignoredChatbotOpenTrackingSources]);

  const { messagesLength, lastMessage } = React.useMemo(() => {
    const { chatMessages = [] } = chatbotChat || {};

    if (!chatMessages.length) {
      return {
        messagesLength: 0,
        lastMessage: null,
      };
    }

    const messagesLength = chatMessages.length;

    const lastMessage = findLastMessage({
      chat: chatbotChat,
      messageIndex: messagesLength - 1,
    });

    return {
      messagesLength,
      lastMessage,
    };
  }, [chatbotChat]);

  const message = React.useMemo(() => {
    if (messagesLength === 0 || !lastMessage || lastMessage.type === 'textAndImages') {
      return 'Найду вам недвижимость';
    }

    if (lastMessage.type === 'offer') {
      return 'А вот и новая подборка - буду присылать такие, пока вы меня не остановите';
    }

    return lastMessage.message;
  }, [messagesLength, lastMessage]);

  if (!isChatbotHelperEnabled || !chatbotChat) {
    return null;
  }

  return (
    <ChatListChatbotItem
      text={message}
      isActive={currentOpenedChatId === CHATBOT_ID}
      onClick={onChatbotClick}
      unreadCount={unreadMessagesCount}
    />
  );
};
